import { pdfRectToCanvasRect } from "../../../../utils/coordinateUtils";

const isPointInsideDisplayedField = (point, field, currentDocument = {}) => {
  const rect = pdfRectToCanvasRect(
    field,
    currentDocument.viewport || currentDocument.actualHeight || 792,
    currentDocument.scaleFactor || 1,
    currentDocument.pageXMin || 0,
    currentDocument.pageYMin || 0,
  );

  return (
    point.x >= rect.left &&
    point.x <= rect.left + rect.width &&
    point.y >= rect.top &&
    point.y <= rect.top + rect.height
  );
};

export const handleMouseDown = (
  e,
  currentProcessingFile,
  setCurrentProcessingFile,
  field,
  scaleFactor,
  direction,
  cursor,
  pdfContainerRef,
  setStartPoint,
  setRect,
  setIsDrawing,
  setCurrCursor,
  setFreeSelectStartPoint,
  setMoving,
  setIsSelecting,
  setInitialMousePosition,
  setInitialInputSize,
  setInputPosition,
  setSelectionRect,
  setResizing,
  setResizeDirection,
  pageNumber,
) => {
  if (direction === "fieldTools") {
    const containerRect = pdfContainerRef?.current.getBoundingClientRect();
    const startX = e.clientX - containerRect.left;
    const startY = e.clientY - containerRect.top;
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};

    // Check if starting inside an existing field
    const isStartingInsideField =
      currentProcessingFile?.document?.tempFields?.formFields.some((field) => {
        if (!field) return false; // Skip null fields
        const rawPage = field?.page_num ?? field?.page;
        const parsedPage = Number(rawPage);
        if (Number.isFinite(parsedPage) && parsedPage !== pageNumber)
          return false;
        return isPointInsideDisplayedField(
          { x: startX, y: startY },
          field,
          currentDocument,
        );
      });

    if (isStartingInsideField) {
      return; // Prevent drawing if starting inside a field
    }

    // Convert to points (1px = 1 / scaleFactor pt)
    setStartPoint({
      x:
        startX /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
      y:
        startY /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
    });

    const returnRect = {
      x:
        startX /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
      y:
        startY /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
      width: 0,
      height: 0,
    };

    setRect(returnRect);
    setIsDrawing(true);
    setCurrCursor("crosshair");
  } else if (direction === "freeSelect") {
    const containerRect = pdfContainerRef?.current.getBoundingClientRect();
    const startX = e.clientX - containerRect.left;
    const startY = e.clientY - containerRect.top;

    // Convert to points (1px = 1 / scaleFactor pt)
    const startPoint = {
      x:
        startX /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
      y:
        startY /
        currentProcessingFile?.document?.tempFields?.currentDocument
          .scaleFactor,
    };

    setFreeSelectStartPoint(startPoint);
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};

    const clickedField = [
      currentProcessingFile?.document?.tempFields?.selectedFormField,
      ...currentProcessingFile?.document?.tempFields?.selectedExtraFields,
    ].find((field) => {
      if (!field) return false; // Skip if `selectedFormField` is null
      return isPointInsideDisplayedField(
        { x: startX, y: startY },
        field,
        currentDocument,
      );
    });

    if (clickedField) {
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      const currentX = e.clientX;
      const currentY = e.clientY;

      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: clickedField, // ✅ Wrap in array to keep consistency
          },
        },
      }));

      setMoving(true);
      setIsSelecting(false);
      setIsDrawing(false); // Ensure drawing is disabled when moving a field
      setSelectionRect(null); // Clear any existing selection rectangle

      setInitialMousePosition({ x: currentX, y: currentY });

      // Use the raw field position without multiplying by scaleFactor here
      setInitialInputSize({
        width: clickedField.width,
        height: clickedField.height,
      });

      setInputPosition({
        x: clickedField.x,
        y: clickedField.y,
      });
    } else {
      const isStartingInsideField =
        currentProcessingFile?.document?.tempFields?.formFields.some(
          (field) => {
            if (!field) return false; // Skip null fields
            const rawPage = field?.page_num ?? field?.page;
            const parsedPage = Number(rawPage);
            if (Number.isFinite(parsedPage) && parsedPage !== pageNumber)
              return false;
            return isPointInsideDisplayedField(
              { x: startX, y: startY },
              field,
              currentDocument,
            );
          },
        );

      if (isStartingInsideField) {
        return; // Prevent selection if starting inside a field
      }
      const returnRect = {
        x:
          startX /
          currentProcessingFile?.document?.tempFields?.currentDocument
            .scaleFactor,
        y:
          startY /
          currentProcessingFile?.document?.tempFields?.currentDocument
            .scaleFactor,
        width: 0,
        height: 0,
      };
      setSelectionRect(returnRect);
      setIsSelecting(true);
    }
  } else if (["nw", "n", "ne", "sw", "s", "se", "w", "e"].includes(direction)) {
    setResizing(true);
    setCurrCursor(cursor);
    setResizeDirection(direction);
    setInitialMousePosition({ x: e.clientX, y: e.clientY });
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};
    const selectedField =
      currentProcessingFile?.document?.tempFields?.selectedFormField?.name ===
      field?.name
        ? currentProcessingFile.document.tempFields.selectedFormField
        : field;
    const displayedRect = pdfRectToCanvasRect(
      selectedField,
      currentDocument.viewport || currentDocument.actualHeight || 792,
      currentDocument.scaleFactor || scaleFactor || 1,
      currentDocument.pageXMin || 0,
      currentDocument.pageYMin || 0,
    );

    setInitialInputSize({
      width: displayedRect.width,
      height: displayedRect.height,
    });
    setInputPosition({
      x: displayedRect.left,
      y: displayedRect.top,
    });
  } else if (direction === "move") {
    setMoving(true);
    setCurrCursor(cursor);
    setResizeDirection(direction);
    setInitialMousePosition({ x: e.clientX, y: e.clientY });

    // Use the raw field position without multiplying by scaleFactor here
    setInitialInputSize({
      width: field.width,
      height: field.height,
    });

    setInputPosition({
      x: field.x,
      y: field.y,
    });
  }
};
